import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { LayoutGrid } from 'lucide-react';
import CompactSelectPicker from './ui/CompactSelectPicker';
import SearchableSelect from './ui/SearchableSelect';
import { workspaceService } from '../services/workspaceService';
import {
  canShareWithTeam,
  isOwnedPersonalCloudWorkspace,
} from '../utils/workspaceShareEligibility';

export default function ShareWithTeamPanel({
  team = null,
  workspace = null,
  teams = [],
  workspaces = [],
  currentUserId,
  onSuccess,
  showToast,
}) {
  const { t } = useTranslation();
  const [selectedTeamId, setSelectedTeamId] = useState('');
  const [selectedWorkspaceId, setSelectedWorkspaceId] = useState('');
  const [nameOverride, setNameOverride] = useState('');
  const [copyVariables, setCopyVariables] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const initRef = useRef(null);

  const shareableTeams = useMemo(
    () => (teams || []).filter(canShareWithTeam),
    [teams]
  );

  const ownedCloudWorkspaces = useMemo(
    () => (workspaces || []).filter((ws) => isOwnedPersonalCloudWorkspace(ws, currentUserId)),
    [workspaces, currentUserId]
  );

  const fixedTeam = team || null;
  const fixedWorkspace = workspace || null;
  const pickTeam = !fixedTeam;
  const pickWorkspace = !fixedWorkspace;

  useEffect(() => {
    const key = `${fixedTeam?.id ?? ''}:${fixedWorkspace?.id ?? ''}`;
    if (initRef.current === key) return;
    initRef.current = key;

    setError('');
    setLoading(false);
    setCopyVariables(true);
    setSelectedTeamId(
      fixedTeam ? String(fixedTeam.id) : String(shareableTeams[0]?.id || '')
    );
    setSelectedWorkspaceId(
      fixedWorkspace ? String(fixedWorkspace.id) : String(ownedCloudWorkspaces[0]?.id || '')
    );
    setNameOverride(fixedWorkspace?.name || '');
  }, [fixedTeam, fixedWorkspace, shareableTeams, ownedCloudWorkspaces]);

  const selectedWorkspace = useMemo(() => {
    const id = fixedWorkspace?.id ?? selectedWorkspaceId;
    return (workspaces || []).find((ws) => String(ws.id) === String(id)) || fixedWorkspace;
  }, [workspaces, fixedWorkspace, selectedWorkspaceId]);

  useEffect(() => {
    if (fixedWorkspace) return;
    if (selectedWorkspace && !nameOverride) {
      setNameOverride(selectedWorkspace.name || '');
    }
  }, [fixedWorkspace, selectedWorkspace, nameOverride]);

  const teamOptions = shareableTeams.map((tm) => ({
    value: String(tm.id),
    label: tm.name,
  }));

  const workspaceOptions = ownedCloudWorkspaces.map((ws) => ({
    id: ws.id,
    url: String(ws.id),
    fullName: ws.name,
    name: ws.name,
    description: ws.description,
  }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    const teamId = fixedTeam?.id ?? selectedTeamId;
    const workspaceId = fixedWorkspace?.id ?? selectedWorkspaceId;

    if (!teamId) {
      setError(t('shareWithTeam.needTeam'));
      return;
    }
    if (!workspaceId) {
      setError(t('shareWithTeam.needWorkspace'));
      return;
    }

    setLoading(true);
    try {
      const shared = await workspaceService.shareWithTeam(workspaceId, {
        team_id: Number(teamId) || teamId,
        name: nameOverride.trim() || undefined,
        fork_copy_variables: copyVariables,
      });
      showToast?.(t('shareWithTeam.success'), 'success');
      await onSuccess?.({ teamId: Number(teamId) || teamId, workspace: shared });
    } catch (err) {
      setError(err.message || t('shareWithTeam.failed'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {error && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
          {error}
        </div>
      )}

      {pickTeam && (
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-zinc-300 mb-2">
            {t('shareWithTeam.teamLabel')}
          </label>
          {shareableTeams.length === 0 ? (
            <p className="text-sm text-amber-600">{t('shareWithTeam.noEligibleTeams')}</p>
          ) : (
            <div className="modal-picker" style={{ display: 'flex', width: '100%' }}>
              <CompactSelectPicker
                value={selectedTeamId}
                onChange={setSelectedTeamId}
                options={teamOptions}
                aria-label={t('shareWithTeam.teamLabel')}
                disabled={loading}
              />
            </div>
          )}
          <p className="mt-1 text-xs text-gray-500">{t('shareWithTeam.teamHint')}</p>
        </div>
      )}

      {pickWorkspace && (
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-zinc-300 mb-2">
            {t('shareWithTeam.workspaceLabel')}
          </label>
          {ownedCloudWorkspaces.length === 0 ? (
            <p className="text-sm text-gray-500">{t('shareWithTeam.noOwnedWorkspaces')}</p>
          ) : (
            <SearchableSelect
              options={workspaceOptions}
              value={selectedWorkspaceId}
              onChange={(id) => {
                setSelectedWorkspaceId(id);
                const ws = ownedCloudWorkspaces.find((w) => String(w.id) === String(id));
                if (ws) setNameOverride(ws.name || '');
              }}
              getKey={(ws) => ws.url}
              getLabel={(ws) => ws.fullName}
              placeholder={t('shareWithTeam.workspacePlaceholder')}
              searchPlaceholder={t('shareWithTeam.workspaceSearch')}
              leadingIcon={<LayoutGrid size={16} className="text-gray-400" />}
              disabled={loading}
            />
          )}
        </div>
      )}

      <div>
        <label htmlFor="share_team_name" className="block text-sm font-medium text-gray-700 dark:text-zinc-300 mb-2">
          {t('shareWithTeam.nameLabel')}
        </label>
        <input
          id="share_team_name"
          type="text"
          value={nameOverride}
          onChange={(e) => setNameOverride(e.target.value)}
          placeholder={selectedWorkspace?.name || t('shareWithTeam.namePlaceholder')}
          className="modal-input modal-input-flat"
          disabled={loading || (!pickWorkspace && !fixedWorkspace)}
        />
        <p className="mt-1 text-xs text-gray-500">{t('shareWithTeam.nameHint')}</p>
      </div>

      <div className="flex items-start space-x-2 pt-1">
        <input
          type="checkbox"
          id="share_copy_variables"
          checked={copyVariables}
          onChange={(e) => setCopyVariables(e.target.checked)}
          disabled={loading}
          className="checkbox-brand cursor-pointer mt-0.5"
        />
        <label htmlFor="share_copy_variables" className="text-sm text-gray-700 dark:text-zinc-300 cursor-pointer">
          {t('shareWithTeam.copyVariables')}
        </label>
      </div>

      <button
        type="submit"
        disabled={
          loading ||
          (pickTeam && shareableTeams.length === 0) ||
          (pickWorkspace && ownedCloudWorkspaces.length === 0)
        }
        className="flex w-full items-center justify-center space-x-2 rounded-lg bg-blue-600 px-4 py-3 text-sm font-medium text-white transition-colors hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
      >
        <span>{loading ? t('shareWithTeam.sharing') : t('shareWithTeam.shareBtn')}</span>
      </button>
    </form>
  );
}
